// src/context/GreetingsModalProvider.tsx
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useLocation, useNavigate, useNavigationType, UNSAFE_NavigationContext } from 'react-router-dom';
import { GreetingsModal } from 'components/organisms/GreetingsModal';
import type { History, Transition } from 'history';

declare module 'history' {
    interface History {
        block(callback: (tx: { retry: () => void }) => void): () => void;
    }
}

interface GreetingsModalContextType {
    showGreetingsModal: boolean;
    hideGreetingsModal: () => void;
    registrationToken: string;
    phoneNumber: string;
    updatePhoneNumber: (phone: string) => void;
}

const GreetingsModalContext = createContext<GreetingsModalContextType>({
    showGreetingsModal: false,
    hideGreetingsModal: () => { },
    registrationToken: '',
    phoneNumber: '',
    updatePhoneNumber: () => { },
});

export const useGreetingsModal = () => useContext(GreetingsModalContext);

export const GreetingsModalProvider = ({ children }: { children: React.ReactNode }) => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [showGreetingsModal, setShowGreetingsModal] = useState(false);
    const [originalPath, setOriginalPath] = useState('');
    const [registrationToken, setRegistrationToken] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');

    const { navigator } = useContext(UNSAFE_NavigationContext);
    const [blocker, setBlocker] = useState<(() => void) | null>(null);
    const isNavigatingRef = useRef(false);

    useEffect(() => {
        if (showGreetingsModal) {
            const unblock = (navigator as unknown as History).block((tx: Transition) => {
                if (isNavigatingRef.current) {
                    return;
                }

                const isSignUpNavigation = tx.location.pathname.startsWith('/auth/sign-up');

                if (isSignUpNavigation) {
                    isNavigatingRef.current = true;
                    setTimeout(() => {
                        tx.retry();
                        isNavigatingRef.current = false;
                    }, 100);
                }
            });

            setBlocker(() => unblock);

            return () => {
                unblock();
                setBlocker(null);
            };
        }
    }, [showGreetingsModal, navigator]);

    useEffect(() => {
        const tokenParam = searchParams.get('token');

        if (tokenParam) {
            setOriginalPath(location.pathname !== '/' ? location.pathname : '/estates/grid');
            setShowGreetingsModal(true);
            setRegistrationToken(tokenParam);

            const newSearchParams = new URLSearchParams(searchParams.toString());
            newSearchParams.delete('token');
            const newSearch = newSearchParams.toString();

            window.history.replaceState({}, '', newSearch ? `${location.pathname}?${newSearch}` : location.pathname);
        }
    }, [searchParams, location, showGreetingsModal]);

    const handleCloseModal = useCallback((navigateToSignUp: boolean = false) => {
        setShowGreetingsModal(false);

        if (navigateToSignUp) {
            navigate('/auth/sign-up/landlord', {
                state: {
                    registrationToken,
                    phoneNumber: phoneNumber || ''
                },
                replace: true
            });
        } else if (originalPath) {
            setRegistrationToken('');
            setPhoneNumber('');
            navigate(originalPath);
        }
    }, [navigate, originalPath, registrationToken, phoneNumber]);

    const updatePhoneNumber = useCallback((phone: string) => {
        setPhoneNumber(phone);
    }, []);

    const contextValue = {
        showGreetingsModal,
        hideGreetingsModal: () => handleCloseModal(false),
        registrationToken,
        phoneNumber,
        updatePhoneNumber
    };

    return (
        <GreetingsModalContext.Provider value={contextValue}>
            {children}
            {showGreetingsModal && (
                <GreetingsModal
                    onClose={handleCloseModal}
                    registrationToken={registrationToken}
                />
            )}
        </GreetingsModalContext.Provider>
    );
};